<template>
  <!--
    `ignore-filter` because the suggestion has to survive the search: Reka's own
    filtering drops the suggested row the moment the user types anything that
    does not match it, which is exactly when a suggestion is worth keeping in
    view. So the rest of the rows are filtered here instead — with Reka's own
    matcher, not a hand-rolled one — and the suggestion is pinned above them.
  -->
  <Combobox
    v-slot="{ open }"
    :model-value
    :disabled
    ignore-filter
    open-on-click
    open-on-focus
    @update:model-value="handlePick"
  >
    <ComboboxAnchor :class="props.class">
      <ComboboxInput
        :id
        :placeholder
        :display-value
        @input="handleSearch($event, open)"
        @blur="emit('blur')"
      />
      <!--
        The spinner sits in the trigger because the pending row below it is only
        visible with the panel open — and the question is asked when the step
        opens, not when the list does.
      -->
      <ComboboxTrigger>
        <Loader2
          v-if="loading"
          class="size-4 animate-spin motion-reduce:animate-none text-brand"
        />
        <ChevronDown
          v-else
          class="size-4"
        />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxList>
      <!--
        The ask, first of all: nothing is requested until the user asks for it,
        so this is what stands where the suggestion will eventually pin itself.
        A plain button rather than a ComboboxItem for the same reason the
        pending row below it is a `<p>` — there is nothing to select yet, and a
        ComboboxItem would register itself with the list's keyboard navigation
        and fire `update:modelValue` on click.
      -->
      <button
        v-if="askable"
        type="button"
        class="flex w-full items-center gap-2.5 px-2.5 py-2 text-left text-sm font-bold text-brand
          bg-accent transition-colors duration-ds-fast ease-ds-standard
          hover:bg-accent-foreground/10
          focus-visible:outline-none focus-visible:shadow-ds-focus
          cursor-pointer"
        @click="emit('ask')"
      >
        <Sparkles class="size-3.5 shrink-0" />
        {{ askLabel }}
      </button>

      <!--
        The suggestion, first and marked as one, with its grounds. A suggestion
        whose value is not among the options is not drawn at all — a row
        pointing at something unpickable is worse than none.
      -->
      <!--
        Where the suggestion will land, held open while it is being decided. The
        row is not selectable — there is nothing to select yet — so it is a
        plain element rather than a ComboboxItem, which would register itself
        with the list's keyboard navigation.
      -->
      <p
        v-if="loading"
        class="flex items-start gap-3 px-2.5 py-2 bg-accent"
      >
        <Thinking
          :label="loadingLabel"
          icon-class="size-4"
          class="flex-1 min-w-0 text-xs"
        />
      </p>

      <ComboboxItem
        v-else-if="suggested"
        :value="suggested.value"
        :text-value="suggested.searchText ?? `${suggested.label} ${suggested.hint ?? ''}`"
        class="items-start gap-3 bg-accent text-accent-foreground data-highlighted:bg-accent-foreground/10"
      >
        <Sparkles class="size-3.5 shrink-0 mt-1 text-brand" />
        <span class="flex-1 min-w-0">
          <span class="block truncate text-sm font-bold">
            {{ suggested.label }}<template v-if="suggested.hint"> · {{ suggested.hint }}</template>
          </span>
          <span class="block text-xs font-medium text-accent-foreground/85">
            {{ suggestion?.reason }}
          </span>
        </span>
        <ArrowRight class="size-3.5 shrink-0 mt-1 text-brand" />
      </ComboboxItem>

      <ComboboxItem
        v-for="option in offered"
        :key="option.value"
        :value="option.value"
        :text-value="option.searchText ?? `${option.label} ${option.hint ?? ''}`"
      >
        <span class="truncate text-base font-medium">{{ option.label }}</span>
        <span
          v-if="option.hint"
          class="shrink-0 text-xs font-medium text-muted-foreground"
        >
          {{ option.hint }}
        </span>
      </ComboboxItem>

      <!--
        Ours rather than ComboboxEmpty: with `ignore-filter` Reka believes every
        row is visible, so only this component knows the search matched nothing.
        An empty search and an empty list are different messages.
      -->
      <p
        v-if="!loading && !suggested && !offered.length"
        class="px-2.5 py-4 text-center text-sm font-medium text-muted-foreground"
      >
        {{ options.length ? emptySearchLabel : emptyLabel }}
      </p>

      <!--
        The scope of the list, at the foot of the panel where it answers "what
        can I even pick?". The caller supplies it, because the caller is what
        narrowed the list.
      -->
      <p
        v-else-if="$slots.note"
        class="flex items-start gap-3 bg-background px-2.5 py-2"
      >
        <Filter class="size-3.5 shrink-0" />
        <span class="text-xs">
          <slot name="note" />
        </span>
      </p>
    </ComboboxList>
  </Combobox>
</template>

<script setup lang="ts">
import { computed, ref, type HTMLAttributes } from 'vue'
import { ArrowRight, ChevronDown, Filter, Loader2, Sparkles } from '@lucide/vue'
import { useFilter } from 'reka-ui'

import {
  Combobox,
  ComboboxAnchor,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '../../primitives/combobox'
import { Thinking } from '../Thinking'
import { ComboboxSuggestion, SuggestionComboboxOption, searchOptions } from './utils'

/*
 * A combobox that can carry one suggested row: pinned above the rest, marked
 * with the sparkle, showing the grounds for suggesting it, and exempt from the
 * search so it stays in view while the user looks for something else. It can
 * also offer to ask for that suggestion in the first place — see `askable` —
 * since none of the three assistants this pattern serves ask on their own
 * anymore; the user asks by clicking the row.
 *
 * A pattern rather than a primitive because that arrangement is a composition —
 * and because the pinning cannot be expressed through Reka's own filtering at
 * all. Where the options and the suggestion come from is the caller's business;
 * this only draws them.
 */
const props = defineProps<{
  modelValue?: string
  options: SuggestionComboboxOption[]
  /** Pinned above the list. Ignored when its value is not among the options. */
  suggestion?: ComboboxSuggestion
  placeholder?: string
  /** Shown when the search matches nothing. */
  emptySearchLabel?: string
  /** Shown when there was nothing to search in the first place. */
  emptyLabel?: string
  /**
   * A suggestion is on its way. Draws the pinned row as pending rather than
   * leaving the list looking like one nothing was suggested for.
   */
  loading?: boolean
  /** What is being decided, said in the pending row. */
  loadingLabel?: string
  /**
   * Offers the "Ask AI" row in place of a suggestion. The caller decides when
   * this is true — typically "nothing suggested or pending yet" — since asking
   * again once an answer exists just repeats it.
   */
  askable?: boolean
  /** Said on the ask row. */
  askLabel?: string
  /** `id` of the input, so a caller's label can point at it. */
  id?: string
  disabled?: boolean
  class?: HTMLAttributes['class']
}>()

const {
  emptySearchLabel = 'Nothing matches that.',
  emptyLabel = 'Nothing to pick.',
  loadingLabel = 'Looking for a suggestion…',
  askLabel = 'Ask AI',
} = props

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  /** The row itself — a caller often needs more of it than its value. */
  (e: 'select', option: SuggestionComboboxOption): void
  (e: 'blur'): void
  /** The "Ask AI" row was clicked. */
  (e: 'ask'): void
}>()

/** Reka's own matcher — accent-insensitive, so "Reserve" finds "Réserve". */
const { contains } = useFilter({ sensitivity: 'base' })

/*
 * What the user typed. Held here because the filtering is ours: Reka keeps its
 * search term to itself, and the root slot exposes only `open` and the value.
 */
const query = ref('')

const suggested = computed(() =>
  props.options.find(option => option.value === props.suggestion?.value),
)

const offered = computed(() =>
  searchOptions(props.options, query.value, contains, suggested.value?.value),
)

/** The combobox holds a value; the field shows the label it belongs to. */
const displayValue = (value: unknown) =>
  props.options.find(option => option.value === value)?.label ?? ''

/*
 * Reka clears the input's own text when the list closes or a value is picked,
 * and it does not tell us — so the search is dropped with the list rather than
 * left filtering a panel nobody is looking at.
 */
const handleSearch = (event: Event, open: boolean) => {
  query.value = open ? (event.target as HTMLInputElement).value : ''
}

const handlePick = (next: unknown) => {
  const option = props.options.find(item => item.value === next)
  if (!option) return

  query.value = ''

  emit('update:modelValue', option.value)
  emit('select', option)
}
</script>
